/**
 * Wizard Step Indicator
 * 
 * Progress indicator for the Create Address Wizard.
 * Shows current step, completed steps, and remaining steps.
 * 
 * @module addresses/wizard
 */

import React from 'react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';

interface Step {
  id: number;
  label: string;
}

interface WizardStepIndicatorProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}

export const WizardStepIndicator: React.FC<WizardStepIndicatorProps> = ({
  steps,
  currentStep,
  className,
}) => {
  return (
    <nav 
      aria-label="Wizard progress" 
      className={cn("w-full", className)}
    >
      {/* Progress bar */}
      <div className="relative">
        {/* Background track */}
        <div className={cn("absolute top-4 left-0 w-full h-1 rounded-full", semanticTokens.bg.muted)} />
        
        {/* Progress fill */}
        <div 
          className="absolute top-4 left-0 h-1 bg-primary rounded-full transition-all duration-240 ease-out"
          style={{ 
            width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` 
          }}
        />
        
        {/* Step circles */}
        <ol className="relative flex justify-between">
          {steps.map((step, idx) => {
            const isActive = step.id === currentStep;
            const isComplete = step.id < currentStep;
            
            return (
              <li key={step.id} className="flex flex-col items-center">
                {/* Circle */}
                <div
                  className={cn(
                    "relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-all duration-240",
                    isComplete && "bg-primary text-primary-foreground",
                    isActive && "bg-primary text-primary-foreground ring-4 ring-primary/20",
                    !isComplete && !isActive && cn(semanticTokens.bg.muted, semanticTokens.fg.subtle)
                  )}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isComplete ? (
                    <SFIcon icon={sfIcons.check} size="sm" aria-hidden="true" />
                  ) : (
                    step.id
                  )}
                </div>
                
                {/* Label */}
                <span 
                  className={cn(
                    "mt-2 text-xs font-medium transition-colors duration-240",
                    isActive ? semanticTokens.fg.default : semanticTokens.fg.muted,
                    "hidden sm:block" // Hide on mobile to save space
                  )}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      
      {/* Mobile: Show current step text */}
      <div className="mt-4 text-center sm:hidden">
        <span className={cn("text-sm font-medium", semanticTokens.fg.default)}>
          Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.label}
        </span>
      </div>
    </nav>
  );
};

export default WizardStepIndicator;

